/* eslint-disable max-lines */
import { inject, Injectable } from '@angular/core';

import { TimeService } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import {
  AutomationActionType,
  AutomationConditionDateConfig,
  AutomationConditionType,
  AutomationTriggerType,
} from '@core/workflow-automations-types';

interface ActionDescriptions {
  [AutomationActionType.CandidateSendEmail]: () => string;
  [AutomationActionType.CandidateAddNote]: () => string;
  [AutomationActionType.CandidateAddTask]: () => string;
  [AutomationActionType.CandidateSendToTellentHR]: () => string;
  [AutomationActionType.CandidateAddTags]: (tags: string[]) => string;
  [AutomationActionType.CandidateAddSourceTags]: (sourceTags: string[]) => string;
  [AutomationActionType.CandidateCancelEvaluationRequests]: () => string;
  [AutomationActionType.CandidateCancelScheduledEvents]: () => string;
  [AutomationActionType.CandidateMoveToTheNextStage]: () => string;
  [AutomationActionType.CandidateEvaluationRequest]: (count: number) => string;
  [AutomationActionType.CandidateAddFollowers]: (followers: string[]) => string;
  [AutomationActionType.CandidateAssignTo]: (args: {
    jobs: string[];
    talentPools: string[];
  }) => string;
  [AutomationActionType.CandidateUnassignFromJob]: (args: {
    jobs: string[];
    talentPools: string[];
    includeSelf: boolean;
  }) => string;
  [AutomationActionType.CandidateShare]: (admins: string[]) => string;
  [AutomationActionType.CandidateUnshare]: (admins: string[]) => string;
  [AutomationActionType.CandidateDisqualify]: (disqualifyReason: string | null) => string;
  [AutomationActionType.JobAddTags]: (tags: string[]) => string;
  [AutomationActionType.JobAddNote]: () => string;
  [AutomationActionType.JobAddTeamMembers]: (admins: string[]) => string;
  [AutomationActionType.JobChangeStatus]: (statusName: string | null) => string;
  [AutomationActionType.JobSubmitToJobBoards]: (codenames: string[]) => string;
}

interface ConditionDescriptions {
  [AutomationConditionType.CandidateHasTask]: () => string;
  [AutomationConditionType.CandidateHasNote]: () => string;
  [AutomationConditionType.CandidateHired]: () => string;
  [AutomationConditionType.CandidateOverdue]: () => string;
  [AutomationConditionType.CandidateTasksCompleted]: () => string;
  [AutomationConditionType.CandidateEventOrganizer]: (organizer: string) => string;
  [AutomationConditionType.CandidatePreferredLocation]: (location: string) => string;
  [AutomationConditionType.CandidateTags]: (tags: string[]) => string;
  [AutomationConditionType.CandidateEventInterviewersInclude]: (interviewers: string[]) => string;
  [AutomationConditionType.CandidateTaskAssigneesInclude]: (assignees: string[]) => string;
  [AutomationConditionType.CandidateSourceTags]: (sourceTag: string) => string;
  [AutomationConditionType.CandidateOrigin]: (origin: string) => string;
  [AutomationConditionType.CandidateMovedFromStage]: (stage: string) => string;
  [AutomationConditionType.CandidateStartDate]: (
    dateConfig: AutomationConditionDateConfig,
  ) => string;
  [AutomationConditionType.CandidateTaskDueDate]: (
    dateConfig: AutomationConditionDateConfig,
  ) => string;
  [AutomationConditionType.CandidateEventDate]: (
    dateConfig: AutomationConditionDateConfig,
  ) => string;
  [AutomationConditionType.CandidateEvaluationScore]: (rating: string) => string;
  [AutomationConditionType.CandidateStage]: (stage: string) => string;
  [AutomationConditionType.CandidateAllEvaluationAreCompleted]: () => string;
  [AutomationConditionType.JobLocation]: (locations: string[]) => string;
  [AutomationConditionType.JobDepartment]: (department: string) => string;
  [AutomationConditionType.JobRecruiter]: (recruiter: string) => string;
  [AutomationConditionType.JobHiringManager]: (hiringManager: string) => string;
  [AutomationConditionType.JobWorkModel]: (workModels: string[]) => string;
  [AutomationConditionType.JobCreator]: (creator: string) => string;
  [AutomationConditionType.JobPreviousStatus]: (status: string) => string;
}

export interface TriggerDescriptions {
  [AutomationTriggerType.CandidateMoved]: (stageName: string) => string;
  [AutomationTriggerType.CandidateDisqualified]: (disqualifyReasonName: string) => string;
  [AutomationTriggerType.CandidateOverdue]: () => string;
  [AutomationTriggerType.CandidateEvaluationStatusChanged]: (status: string) => string;
  [AutomationTriggerType.CandidateEvaluationStatusChanged]: (status: string) => string;
  [AutomationTriggerType.CandidateEventHasPassed]: () => string;
  [AutomationTriggerType.CandidateEventScheduled]: () => string;
  [AutomationTriggerType.CandidateEventRescheduled]: () => string;
  [AutomationTriggerType.CandidateEventDeleted]: () => string;
  [AutomationTriggerType.CandidateNoteAdded]: () => string;
  [AutomationTriggerType.CandidateMarkedAsHired]: () => string;
  [AutomationTriggerType.CandidateTaskAdded]: () => string;
  [AutomationTriggerType.CandidateAllTasksCompleted]: () => string;
  [AutomationTriggerType.CandidateRequalified]: () => string;
  [AutomationTriggerType.CandidateStageChanged]: () => string;
  [AutomationTriggerType.CandidateAssignedToThisJob]: () => string;
  [AutomationTriggerType.CandidateTagAdded]: (tags: string[]) => string;
  [AutomationTriggerType.JobStatusChanged]: (status: string) => string;
  [AutomationTriggerType.JobOpeningsFilled]: () => string;
  [AutomationTriggerType.JobTagAdded]: (tags: string[]) => string;
  [AutomationTriggerType.JobNoteAdded]: () => string;
  [AutomationTriggerType.JobDateHasPassed]: (date: string) => string;
  [AutomationTriggerType.CandidateStartDateIs]: (data: {
    timeRange: string | null;
    amount: string | null;
    unit: string | null;
  }) => string;
}

@Injectable({ providedIn: 'root' })
export class AutomationDictionaryService {
  private readonly i18n = inject(I18nService);
  private readonly timeService = inject(TimeService);

  public readonly translations = {
    // ACTIONS
    sendEmail: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.send_email.one',
    ),
    addNote: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.add_note.one',
    ),
    addTask: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.add_task.one',
    ),
    addTags: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.add_tags_v2.basic_action',
    ),
    addOneTag: (tagName: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.add_tags_v2.one_action',
        { tagName },
      ),
    addTwoTags: (tagName1: string, tagName2: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.add_tags_v2.two_actions',
        { tagName1, tagName2 },
      ),
    addManyTags: (tagNames: string, count: number) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.add_tags_v2.many_actions',
        { tagNames, count },
      ),
    addSourceTags: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.add_source_tags.basic_action',
    ),
    addOneSourceTag: (tagName: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.add_source_tags.one_action',
        { tagName },
      ),
    addTwoSourceTags: (tagName1: string, tagName2: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.add_source_tags.two_actions',
        { tagName1, tagName2 },
      ),
    addManySourceTags: (tagNames: string, count: number) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.add_source_tags.many_actions',
        { tagNames, count },
      ),
    cancelEvaluationRequests: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.cancel_evaluation_requests.one_action',
    ),
    cancelScheduledEvents: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.cancel_scheduled_events.one_action',
    ),
    moveToTheNextStage: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.move_to_the_next_stage.one_action',
    ),
    addOneEvaluationRequest: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.evaluation_request_v2.one_action',
    ),
    addManyEvaluationRequests: (count: number) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.evaluation_request_v2.many_actions',
        { count },
      ),
    addFollowers: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.add_followers_v2.basic_action',
    ),
    addOneFollower: (name: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.add_followers_v2.one_action',
        { name },
      ),
    addTwoFollowers: (name1: string, name2: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.add_followers_v2.two_actions',
        { name1, name2 },
      ),
    addManyFollowers: (names: string, count: number) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.add_followers_v2.many_actions',
        { names, count },
      ),
    assignThem: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.assign_to.assign_them',
    ),
    unassignThem: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.unassign.unassign_them',
    ),
    and: this.i18n.translate('app.common.and'),
    andThe: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.assign_to.and_the',
    ),
    thisJob: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.unassign.this_job',
    ),
    assignToJobOrTalentPool: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.assign_to.basic_action',
    ),
    unassignFromJobOrTalentPool: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.unassign.basic_action',
    ),
    moreJobs: (count: number) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.assign_to.more_jobs',
        { count },
      ),
    moreTalentPools: (count: number) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.assign_to.more_talent_pools',
        { count },
      ),
    jobsPlurals: (count: number) => this.i18n.translate('app.plurals.job', { count }),
    daysPlurals: (count: number) => this.i18n.translate('app.plurals.day', { count }),
    weeksPlurals: (count: number) => this.i18n.translate('app.plurals.week', { count }),
    monthsPlurals: (count: number) => this.i18n.translate('app.plurals.month', { count }),
    quartersPlurals: (count: number) => this.i18n.translate('app.plurals.quarter', { count }),
    yearsPlurals: (count: number) => this.i18n.translate('app.plurals.year', { count }),
    talentPoolsPlurals: (count: number) =>
      this.i18n.translate('app.plurals.talent_pool', { count }),
    addTeamMembers: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.add_team_members.basic_action',
    ),
    addOneTeamMember: (name: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.add_team_members.one_action',
        { name },
      ),
    addTwoTeamMembers: (name1: string, name2: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.add_team_members.two_actions',
        { name1, name2 },
      ),
    addManyTeamMembers: (names: string, count: number) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.add_team_members.many_actions',
        { names, count },
      ),
    shareCandidateBasic: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.share_candidate.basic_action',
    ),
    shareCandidateOneTeamMember: (name: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.share_candidate.one_action',
        { name },
      ),
    shareCandidateTwoTeamMembers: (name1: string, name2: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.share_candidate.two_actions',
        { name1, name2 },
      ),
    shareCandidateManyTeamMembers: (names: string, count: number) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.share_candidate.many_actions',
        { names, count },
      ),
    unshareCandidateBasic: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.unshare_candidate.basic_action',
    ),
    unshareCandidateOneTeamMember: (name: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.unshare_candidate.one_action',
        { name },
      ),
    unshareCandidateTwoTeamMembers: (name1: string, name2: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.unshare_candidate.two_actions',
        { name1, name2 },
      ),
    unshareCandidateManyTeamMembers: (names: string, count: number) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.unshare_candidate.many_actions',
        { names, count },
      ),
    disqualifyCandidateBasic: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.disqualify_candidate.basic_action',
    ),
    disqualifyCandidateOneReason: (disqualifyReason: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.disqualify_candidate.one_action',
        { disqualifyReason },
      ),
    changeJobStatusBasic: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.change_job_status.basic_action',
    ),
    changeJobStatus: (statusName: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.change_job_status.one_action',
        { statusName },
      ),
    sendToTellentHr: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.send_to_tellent_hr.one_action',
    ),
    submitToJobBoardsBasic: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.actions.submit_to_job_boards.basic_action',
    ),
    submitToJobBoardsOne: (jobBoard: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.submit_to_job_boards.one_action',
        { jobBoard },
      ),
    submitToJobBoardsTwo: (jobBoard1: string, jobBoard2: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.submit_to_job_boards.two_actions',
        { jobBoard1, jobBoard2 },
      ),
    submitToJobBoardsMany: (jobBoardNames: string, count: number) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.actions.submit_to_job_boards.many_actions',
        { jobBoardNames, count },
      ),

    // TRIGGERS
    candidateMoved: (stageName: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.triggers.candidate_moved',
        { stageName },
      ),
    candidateDisqualified: (disqualifyReasonName: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.triggers.candidate_disqualified',
        { disqualifyReasonName },
      ),
    candidateOverdue: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_overdue',
    ),
    candidateEventHasPassed: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_event_has_passed',
    ),
    candidateEventScheduled: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_event_scheduled',
    ),
    candidateEventRescheduled: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_event_rescheduled',
    ),
    candidateEventDeleted: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_event_deleted',
    ),
    candidateNoteAdded: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_note_added',
    ),
    candidateIsTagged: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_is_tagged',
    ),
    candidateMarkedAsHired: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_marked_as_hired',
    ),
    candidateTaskAdded: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_task_added',
    ),
    candidateAllTasksCompleted: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_all_tasks_completed',
    ),
    candidateRequalified: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_requalified',
    ),
    candidateAssignedToThisJob: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_assigned_to_this_job',
    ),
    candidateStageChanged: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_stage_changed',
    ),
    candidateEvaluationStatusChanged: (status: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.triggers.candidate_evaluation_status_changed',
        { status },
      ),
    jobStatusChanged: (status: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.triggers.job_status_changed',
        { status },
      ),
    jobDateHasPassed: (date: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.triggers.job_date_has_passed',
        { date },
      ),
    jobOpeningsFilled: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.job_openings_filled',
    ),
    jobNoteAdded: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.job_note_added',
    ),
    jobIsTagged: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.job_is_tagged',
    ),
    isTaggedOne: (tagName: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.triggers.is_tagged_v2.one_tag',
        { tagName },
      ),
    isTaggedTwo: (tagName1: string, tagName2: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.triggers.is_tagged_v2.two_tags',
        { tagName1, tagName2 },
      ),
    isTaggedMany: (tagNames: string, count: number) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.triggers.is_tagged_v2.many_tags',
        { tagNames, count },
      ),
    startDateBasic: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_start_date_is.basic',
    ),
    in: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_start_date_is.in',
    ),
    reached: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_start_date_is.reached',
    ),
    within: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.triggers.candidate_start_date_is.within',
    ),
  };

  public readonly conditionTranslations = {
    andOnlyIf: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.conditions.and_only_if',
    ),
    onlyIf: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.conditions.only_if',
    ),
    and: this.i18n.translate('app.common.and'),
    candidateHasTask: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.conditions.candidate_has_task',
    ),
    candidateHasNote: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.conditions.candidate_has_note',
    ),
    candidateHired: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.conditions.candidate_hired',
    ),
    candidateOverdue: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.conditions.candidate_overdue',
    ),
    candidateTasksCompleted: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.conditions.candidate_tasks_completed',
    ),
    candidateEventOrganizer: (organizer: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.candidate_event_organizer',
        { organizer },
      ),
    candidatePreferredLocation: (location: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.candidate_preferred_location',
        { location },
      ),
    candidateTags: (tags: string[]) => {
      if (!tags?.length)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_tags.zero_tags',
        );
      if (tags.length === 1)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_tags.one_tag',
          { tagName: tags[0] },
        );
      if (tags.length === 2)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_tags.two_tags',
          { tagName1: tags[0], tagName2: tags[1] },
        );
      return this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.candidate_tags.many_tags',
        {
          tagNames: tags
            .slice(0, 2)
            .map(tag => `"${tag}"`)
            .join(', '),
          count: tags.length - 2,
        },
      );
    },
    candidateEventInterviewersInclude: (interviewers: string[]) => {
      if (!interviewers?.length)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_event_interviewers_include.zero_interviewers',
        );
      if (interviewers.length === 1)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_event_interviewers_include.one_interviewers',
          { interviewer: interviewers[0] },
        );
      if (interviewers.length === 2)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_event_interviewers_include.two_interviewers',
          { interviewer1: interviewers[0], interviewer2: interviewers[1] },
        );
      return this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.candidate_event_interviewers_include.many_interviewers',
        {
          interviewers: interviewers.slice(0, 2).join(', '),
          count: interviewers.length - 2,
        },
      );
    },
    candidateTaskAssigneesInclude: (assignees: string[]) => {
      if (!assignees?.length)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_task_assignees_include.zero_assignees',
        );
      if (assignees.length === 1)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_task_assignees_include.one_assignees',
          { assignee: assignees[0] },
        );
      if (assignees.length === 2)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_task_assignees_include.two_assignees',
          { assignee1: assignees[0], assignee2: assignees[1] },
        );
      return this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.candidate_task_assignees_include.many_assignees',
        {
          assignees: assignees.slice(0, 2).join(', '),
          count: assignees.length - 2,
        },
      );
    },
    candidateSourceTags: (sourceTag: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.candidate_source_tags',
        { sourceTag },
      ),
    candidateOrigin: (origin: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.candidate_origin',
        { origin },
      ),
    candidateMovedFromStage: (stage: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.candidate_moved_from_stage',
        { stage },
      ),
    candidateStartDate: ({
      timeFrame,
      timeFrameValue,
      within,
      period,
      date,
      dateFrom,
      dateTo,
    }: AutomationConditionDateConfig) => {
      if (timeFrameValue === 'within_next') {
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_start_date.within_next',
          { date: `${within || ''} ${period || ''}` },
        );
      }
      if (timeFrameValue === 'within_previous') {
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_start_date.within_previous',
          { date: `${within || ''} ${period || ''}` },
        );
      }

      if (timeFrameValue === 'exact_date') {
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_start_date.basic',
          { date: date ? this.timeService.format({ input: date, pattern: 'DD MMM YYYY' }) : '' },
        );
      }

      if (timeFrameValue === 'date_range') {
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_start_date.range',
          {
            dateFrom: dateFrom
              ? this.timeService.format({ input: dateFrom, pattern: 'DD MMM YYYY' })
              : '',
            dateTo: dateTo
              ? this.timeService.format({ input: dateTo, pattern: 'DD MMM YYYY' })
              : '',
          },
        );
      }

      return this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.candidate_start_date.basic',
        { date: timeFrame || '' },
      );
    },
    candidateTaskDueDate: ({
      timeFrame,
      timeFrameValue,
      within,
      period,
      date,
      dateFrom,
      dateTo,
    }: AutomationConditionDateConfig) => {
      if (timeFrameValue === 'within_next') {
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_task_due_date.within_next',
          { date: `${within || ''} ${period || ''}` },
        );
      }
      if (timeFrameValue === 'within_previous') {
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_task_due_date.within_previous',
          { date: `${within || ''} ${period || ''}` },
        );
      }

      if (timeFrameValue === 'exact_date') {
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_task_due_date.basic',
          { date: date ? this.timeService.format({ input: date, pattern: 'DD MMM YYYY' }) : '' },
        );
      }

      if (timeFrameValue === 'date_range') {
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_task_due_date.range',
          {
            dateFrom: dateFrom
              ? this.timeService.format({ input: dateFrom, pattern: 'DD MMM YYYY' })
              : '',
            dateTo: dateTo
              ? this.timeService.format({ input: dateTo, pattern: 'DD MMM YYYY' })
              : '',
          },
        );
      }

      return this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.candidate_task_due_date.basic',
        { date: timeFrame || '' },
      );
    },
    candidateEventDate: ({
      timeFrame,
      timeFrameValue,
      within,
      period,
      date,
      dateFrom,
      dateTo,
    }: AutomationConditionDateConfig) => {
      if (timeFrameValue === 'within_next') {
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_event_date.within_next',
          { date: `${within || ''} ${period || ''}` },
        );
      }
      if (timeFrameValue === 'within_previous') {
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_event_date.within_previous',
          { date: `${within || ''} ${period || ''}` },
        );
      }

      if (timeFrameValue === 'exact_date') {
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_event_date.basic',
          { date: date ? this.timeService.format({ input: date, pattern: 'DD MMM YYYY' }) : '' },
        );
      }

      if (timeFrameValue === 'date_range') {
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.candidate_event_date.range',
          {
            dateFrom: dateFrom
              ? this.timeService.format({ input: dateFrom, pattern: 'DD MMM YYYY' })
              : '',
            dateTo: dateTo
              ? this.timeService.format({ input: dateTo, pattern: 'DD MMM YYYY' })
              : '',
          },
        );
      }

      return this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.candidate_event_date.basic',
        { date: timeFrame || '' },
      );
    },
    candidateEvaluationScore: (rating: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.candidate_evaluation_score',
        { rating },
      ),
    candidateStage: (stage: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.candidate_stage',
        { stage },
      ),
    candidateAllEvaluationAreCompleted: this.i18n.translate(
      'app.workflow_automations.automation_description_partials.conditions.candidate_all_evaluation_are_completed',
    ),
    jobLocation: (locations: string[]) => {
      if (!locations?.length)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.job_locations.zero_locations',
        );
      if (locations.length === 1)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.job_locations.one_location',
          { location: locations[0] },
        );
      if (locations.length === 2)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.job_locations.two_locations',
          { location1: locations[0], location2: locations[1] },
        );
      return this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.job_locations.many_locations',
        {
          locations: locations.slice(0, 2).join(', '),
          count: locations.length - 2,
        },
      );
    },
    jobDepartment: (department: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.job_department',
        { department },
      ),
    jobRecruiter: (recruiter: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.job_recruiter',
        { recruiter },
      ),
    jobHiringManager: (hiringManager: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.job_hiring_manager',
        { hiringManager },
      ),
    jobWorkModel: (workModels: string[]) => {
      if (!workModels?.length)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.job_work_model.zero_work_models',
        );
      if (workModels.length === 1)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.job_work_model.one_work_model',
          { workModel: workModels[0] },
        );
      if (workModels.length === 2)
        return this.i18n.translate(
          'app.workflow_automations.automation_description_partials.conditions.job_work_model.two_work_models',
          { workModel1: workModels[0], workModel2: workModels[1] },
        );
      return this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.job_work_model.three_work_models',
        {
          workModels: workModels.slice(0, 2).join(', '),
          workModel3: workModels[2],
        },
      );
    },
    jobCreator: (creator: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.job_creator',
        { creator },
      ),
    jobPreviousStatus: (status: string) =>
      this.i18n.translate(
        'app.workflow_automations.automation_description_partials.conditions.job_previous_status',
        { status },
      ),
  };

  public triggerDescriptionsMap: TriggerDescriptions = {
    [AutomationTriggerType.CandidateMoved]: (stageName: string) =>
      this.translations.candidateMoved(stageName),
    [AutomationTriggerType.CandidateDisqualified]: (disqualifyReasonName: string) =>
      this.translations.candidateDisqualified(disqualifyReasonName),
    [AutomationTriggerType.CandidateOverdue]: () => this.translations.candidateOverdue,
    [AutomationTriggerType.CandidateEvaluationStatusChanged]: (status: string) =>
      this.translations.candidateEvaluationStatusChanged(status),
    [AutomationTriggerType.CandidateEventHasPassed]: () =>
      this.translations.candidateEventHasPassed,
    [AutomationTriggerType.CandidateEventScheduled]: () =>
      this.translations.candidateEventScheduled,
    [AutomationTriggerType.CandidateEventRescheduled]: () =>
      this.translations.candidateEventRescheduled,
    [AutomationTriggerType.CandidateEventDeleted]: () => this.translations.candidateEventDeleted,
    [AutomationTriggerType.CandidateNoteAdded]: () => this.translations.candidateNoteAdded,
    [AutomationTriggerType.CandidateMarkedAsHired]: () => this.translations.candidateMarkedAsHired,
    [AutomationTriggerType.CandidateTaskAdded]: () => this.translations.candidateTaskAdded,
    [AutomationTriggerType.CandidateRequalified]: () => this.translations.candidateRequalified,
    [AutomationTriggerType.CandidateStageChanged]: () => this.translations.candidateStageChanged,
    [AutomationTriggerType.CandidateAssignedToThisJob]: () =>
      this.translations.candidateAssignedToThisJob,
    [AutomationTriggerType.CandidateAllTasksCompleted]: () =>
      this.translations.candidateAllTasksCompleted,
    [AutomationTriggerType.CandidateTagAdded]: (tags: string[]) => {
      const basicText = this.translations.candidateIsTagged;
      if (!tags?.length) return basicText;
      if (tags.length === 1) return `${basicText} ${this.translations.isTaggedOne(tags[0])}`;
      if (tags.length === 2)
        return `${basicText} ${this.translations.isTaggedTwo(tags[0], tags[1])}`;
      return `${basicText} ${this.translations.isTaggedMany(
        tags
          .slice(0, 2)
          .map(tag => `"${tag}"`)
          .join(', '),
        tags.length - 2,
      )}`;
    },
    [AutomationTriggerType.JobStatusChanged]: (status: string) =>
      this.translations.jobStatusChanged(status),
    [AutomationTriggerType.JobOpeningsFilled]: () => this.translations.jobOpeningsFilled,
    [AutomationTriggerType.JobTagAdded]: (tags: string[]) => {
      const basicText = this.translations.jobIsTagged;
      if (!tags?.length) return basicText;
      if (tags.length === 1) return `${basicText} ${this.translations.isTaggedOne(tags[0])}`;
      if (tags.length === 2)
        return `${basicText} ${this.translations.isTaggedTwo(tags[0], tags[1])}`;
      return `${basicText} ${this.translations.isTaggedMany(
        tags
          .slice(0, 2)
          .map(tag => `"${tag}"`)
          .join(', '),
        tags.length - 2,
      )}`;
    },
    [AutomationTriggerType.JobNoteAdded]: () => this.translations.jobNoteAdded,
    [AutomationTriggerType.JobDateHasPassed]: (date: string) =>
      this.translations.jobDateHasPassed(date),
    [AutomationTriggerType.CandidateStartDateIs]: data => {
      const { timeRange, amount, unit } = data;
      const basicText = this.translations.startDateBasic;

      if (!timeRange) return basicText;

      switch (timeRange) {
        case 'reached':
          return `${basicText} <b>${this.translations.reached}</b>`;
        case 'in_2_days':
          return `${basicText} <b>${this.translations.in} 2 ${this.translations.daysPlurals(2)}</b>`;
        case 'in_7_days':
          return `${basicText} <b>${this.translations.in} 7 ${this.translations.daysPlurals(7)}</b>`;
        case 'in_14_days':
          return `${basicText} <b>${this.translations.in} 14 ${this.translations.daysPlurals(14)}</b>`;
        case 'in_30_days':
          return `${basicText} <b>${this.translations.in} 30 ${this.translations.daysPlurals(30)}</b>`;
        case 'within_the_next':
          const isAmountCorrectNumber = /^[0-9]+$/.test(amount ?? '');
          if (!isAmountCorrectNumber || !unit)
            return `${basicText} <b>${this.translations.within}</b>`;
          switch (unit) {
            case 'days':
              return `${basicText} <b>${this.translations.within} ${amount} ${this.translations.daysPlurals(+amount!)}</b>`;
            case 'weeks':
              return `${basicText} <b>${this.translations.within} ${amount} ${this.translations.weeksPlurals(+amount!)}</b>`;
            case 'months':
              return `${basicText} <b>${this.translations.within} ${amount} ${this.translations.monthsPlurals(+amount!)}</b>`;
            case 'quarters':
              return `${basicText} <b>${this.translations.within} ${amount} ${this.translations.quartersPlurals(+amount!)}</b>`;
            case 'years':
              return `${basicText} <b>${this.translations.within} ${amount} ${this.translations.yearsPlurals(+amount!)}</b>`;
            default:
              return `${basicText} <b>${this.translations.within}</b>`;
          }
        default:
          return '';
      }
    },
  };

  public conditionDescriptionsMap: ConditionDescriptions = {
    [AutomationConditionType.CandidateHasTask]: () => this.conditionTranslations.candidateHasTask,
    [AutomationConditionType.CandidateHasNote]: () => this.conditionTranslations.candidateHasNote,
    [AutomationConditionType.CandidateHired]: () => this.conditionTranslations.candidateHired,
    [AutomationConditionType.CandidateOverdue]: () => this.conditionTranslations.candidateOverdue,
    [AutomationConditionType.CandidateTasksCompleted]: () =>
      this.conditionTranslations.candidateTasksCompleted,
    [AutomationConditionType.CandidateEventOrganizer]: (organizer: string) =>
      this.conditionTranslations.candidateEventOrganizer(organizer),
    [AutomationConditionType.CandidatePreferredLocation]: (location: string) =>
      this.conditionTranslations.candidatePreferredLocation(location),
    [AutomationConditionType.CandidateTags]: (tags: string[]) =>
      this.conditionTranslations.candidateTags(tags),
    [AutomationConditionType.CandidateEventInterviewersInclude]: (interviewers: string[]) =>
      this.conditionTranslations.candidateEventInterviewersInclude(interviewers),
    [AutomationConditionType.CandidateTaskAssigneesInclude]: (assignees: string[]) =>
      this.conditionTranslations.candidateTaskAssigneesInclude(assignees),
    [AutomationConditionType.CandidateSourceTags]: (sourceTag: string) =>
      this.conditionTranslations.candidateSourceTags(sourceTag),
    [AutomationConditionType.CandidateOrigin]: (origin: string) =>
      this.conditionTranslations.candidateOrigin(origin),
    [AutomationConditionType.CandidateMovedFromStage]: (stage: string) =>
      this.conditionTranslations.candidateMovedFromStage(stage),
    [AutomationConditionType.CandidateStartDate]: (dateConfig: AutomationConditionDateConfig) =>
      this.conditionTranslations.candidateStartDate(dateConfig),
    [AutomationConditionType.CandidateTaskDueDate]: (dateConfig: AutomationConditionDateConfig) =>
      this.conditionTranslations.candidateTaskDueDate(dateConfig),
    [AutomationConditionType.CandidateEventDate]: (dateConfig: AutomationConditionDateConfig) =>
      this.conditionTranslations.candidateEventDate(dateConfig),
    [AutomationConditionType.CandidateEvaluationScore]: (rating: string) =>
      this.conditionTranslations.candidateEvaluationScore(rating),
    [AutomationConditionType.CandidateStage]: (stage: string) =>
      this.conditionTranslations.candidateStage(stage),
    [AutomationConditionType.CandidateAllEvaluationAreCompleted]: () =>
      this.conditionTranslations.candidateAllEvaluationAreCompleted,
    [AutomationConditionType.JobLocation]: (locations: string[]) =>
      this.conditionTranslations.jobLocation(locations),
    [AutomationConditionType.JobDepartment]: (department: string) =>
      this.conditionTranslations.jobDepartment(department),
    [AutomationConditionType.JobRecruiter]: (recruiter: string) =>
      this.conditionTranslations.jobRecruiter(recruiter),
    [AutomationConditionType.JobHiringManager]: (hiringManager: string) =>
      this.conditionTranslations.jobHiringManager(hiringManager),
    [AutomationConditionType.JobWorkModel]: (workModels: string[]) =>
      this.conditionTranslations.jobWorkModel(workModels),
    [AutomationConditionType.JobCreator]: (creator: string) =>
      this.conditionTranslations.jobCreator(creator),
    [AutomationConditionType.JobPreviousStatus]: (status: string) =>
      this.conditionTranslations.jobPreviousStatus(status),
  };

  public actionDescriptionsMap: ActionDescriptions = {
    [AutomationActionType.CandidateSendEmail]: () => this.translations.sendEmail,
    [AutomationActionType.CandidateAddNote]: () => this.translations.addNote,
    [AutomationActionType.CandidateAddTask]: () => this.translations.addTask,
    [AutomationActionType.CandidateSendToTellentHR]: () => this.translations.sendToTellentHr,
    [AutomationActionType.CandidateAddTags]: (tags: string[]) => {
      if (!tags?.length) return this.translations.addTags;
      if (tags.length === 1) return this.translations.addOneTag(tags[0]);
      if (tags.length === 2) return this.translations.addTwoTags(tags[0], tags[1]);
      return this.translations.addManyTags(
        tags
          .slice(0, 2)
          .map(tag => `"${tag}"`)
          .join(', '),
        tags.length - 2,
      );
    },
    [AutomationActionType.CandidateAddSourceTags]: (sourceTags: string[]) => {
      if (!sourceTags?.length) return this.translations.addSourceTags;
      if (sourceTags.length === 1) return this.translations.addOneSourceTag(sourceTags[0]);
      if (sourceTags.length === 2)
        return this.translations.addTwoSourceTags(sourceTags[0], sourceTags[1]);
      return this.translations.addManySourceTags(
        sourceTags
          .slice(0, 2)
          .map(tag => `"${tag}"`)
          .join(', '),
        sourceTags.length - 2,
      );
    },
    [AutomationActionType.CandidateEvaluationRequest]: (count: number) => {
      if (!count) return this.translations.addOneEvaluationRequest;
      if (count === 1) return this.translations.addOneEvaluationRequest;
      return this.translations.addManyEvaluationRequests(count);
    },
    [AutomationActionType.CandidateCancelEvaluationRequests]: () =>
      this.translations.cancelEvaluationRequests,
    [AutomationActionType.CandidateCancelScheduledEvents]: () =>
      this.translations.cancelScheduledEvents,
    [AutomationActionType.CandidateMoveToTheNextStage]: () => this.translations.moveToTheNextStage,
    [AutomationActionType.CandidateAddFollowers]: (followers: string[]) => {
      if (!followers?.length) return this.translations.addFollowers;
      if (followers.length === 1) return this.translations.addOneFollower(followers[0]);
      if (followers.length === 2)
        return this.translations.addTwoFollowers(followers[0], followers[1]);
      return this.translations.addManyFollowers(
        followers.slice(0, 2).join(', '),
        followers.length - 2,
      );
    },
    [AutomationActionType.CandidateAssignTo]: ({ jobs, talentPools }) => {
      const oneJob = `<b>${jobs[0]} ${this.translations.jobsPlurals(1)}</b>`;
      const twoJobs = `<b>${jobs[0]}</b> ${this.translations.and} <b>${jobs[1]} ${this.translations.jobsPlurals(2)}</b>`;
      const manyJobs = `<b>${jobs.slice(0, 2).join(', ')}</b> ${this.translations.and} <b>${this.translations.moreJobs(jobs.length - 2)}</b>`;
      const oneTalentPool = `<b>${talentPools[0]} ${this.translations.talentPoolsPlurals(1)}</b>`;
      const twoTalentPools = `<b>${talentPools[0]}</b> ${this.translations.and} <b>${talentPools[1]} ${this.translations.talentPoolsPlurals(2)}</b>`;
      const manyTalentPools = `<b>${talentPools.slice(0, 2).join(', ')}</b> ${this.translations.and} <b>${this.translations.moreTalentPools(talentPools.length - 2)}</b>`;

      if (!jobs?.length && !talentPools?.length) return this.translations.assignToJobOrTalentPool;
      let jobsPart = '';
      let talentPoolsPart = '';
      if (jobs.length) {
        if (jobs.length === 1) jobsPart = oneJob;
        else if (jobs.length === 2) jobsPart = twoJobs;
        else jobsPart = manyJobs;
      }
      if (talentPools.length) {
        if (talentPools.length === 1) talentPoolsPart = oneTalentPool;
        else if (talentPools.length === 2) talentPoolsPart = twoTalentPools;
        else talentPoolsPart = manyTalentPools;
      }

      return `${this.translations.assignThem} ${[jobsPart, talentPoolsPart].filter(Boolean).join(`,  ${this.translations.andThe} `)}`;
    },
    [AutomationActionType.CandidateUnassignFromJob]: ({ jobs, talentPools, includeSelf }) => {
      const oneJob = `<b>${jobs[0]} ${this.translations.jobsPlurals(1)}</b>`;
      const twoJobs = `<b>${jobs[0]}</b> ${this.translations.and} <b>${jobs[1]} ${this.translations.jobsPlurals(2)}</b>`;
      const manyJobs = `<b>${jobs.slice(0, 2).join(', ')}</b> ${this.translations.and} <b>${this.translations.moreJobs(jobs.length - 2)}</b>`;
      const oneTalentPool = `<b>${talentPools[0]} ${this.translations.talentPoolsPlurals(1)}</b>`;
      const twoTalentPools = `<b>${talentPools[0]}</b> ${this.translations.and} <b>${talentPools[1]} ${this.translations.talentPoolsPlurals(2)}</b>`;
      const manyTalentPools = `<b>${talentPools.slice(0, 2).join(', ')}</b> ${this.translations.and} <b>${this.translations.moreTalentPools(talentPools.length - 2)}</b>`;

      if (!jobs?.length && !talentPools?.length && !includeSelf)
        return this.translations.unassignFromJobOrTalentPool;
      let jobsPart = '';
      let talentPoolsPart = '';
      let includeSelfPart = '';
      if (includeSelf) {
        includeSelfPart = `<b>${this.translations.thisJob}</b>`;
      }
      if (jobs.length) {
        if (jobs.length === 1) jobsPart = oneJob;
        else if (jobs.length === 2) jobsPart = twoJobs;
        else jobsPart = manyJobs;
      }
      if (talentPools.length) {
        if (talentPools.length === 1) talentPoolsPart = oneTalentPool;
        else if (talentPools.length === 2) talentPoolsPart = twoTalentPools;
        else talentPoolsPart = manyTalentPools;
      }

      return `${this.translations.unassignThem} ${[includeSelfPart, jobsPart, talentPoolsPart].filter(Boolean).join(`,  ${this.translations.andThe} `)}`;
    },
    [AutomationActionType.CandidateShare]: (admins: string[]) => {
      if (!admins?.length) return this.translations.shareCandidateBasic;
      if (admins.length === 1) return this.translations.shareCandidateOneTeamMember(admins[0]);
      if (admins.length === 2)
        return this.translations.shareCandidateTwoTeamMembers(admins[0], admins[1]);
      return this.translations.shareCandidateManyTeamMembers(
        admins.slice(0, 2).join(', '),
        admins.length - 2,
      );
    },
    [AutomationActionType.CandidateUnshare]: (admins: string[]) => {
      if (!admins?.length) return this.translations.unshareCandidateBasic;
      if (admins.length === 1) return this.translations.unshareCandidateOneTeamMember(admins[0]);
      if (admins.length === 2)
        return this.translations.unshareCandidateTwoTeamMembers(admins[0], admins[1]);
      return this.translations.unshareCandidateManyTeamMembers(
        admins.slice(0, 2).join(', '),
        admins.length - 2,
      );
    },
    [AutomationActionType.CandidateDisqualify]: (disqualifyReason: string | null) => {
      if (!disqualifyReason) return this.translations.disqualifyCandidateBasic;
      return this.translations.disqualifyCandidateOneReason(disqualifyReason);
    },
    [AutomationActionType.JobAddNote]: () => this.translations.addNote,
    [AutomationActionType.JobAddTags]: (tags: string[]) => {
      if (!tags?.length) return this.translations.addTags;
      if (tags.length === 1) return this.translations.addOneTag(tags[0]);
      if (tags.length === 2) return this.translations.addTwoTags(tags[0], tags[1]);
      return this.translations.addManyTags(tags.slice(0, 2).join(', '), tags.length - 2);
    },
    [AutomationActionType.JobAddTeamMembers]: (admins: string[]) => {
      if (!admins?.length) return this.translations.addTeamMembers;
      if (admins.length === 1) return this.translations.addOneTeamMember(admins[0]);
      if (admins.length === 2) return this.translations.addTwoTeamMembers(admins[0], admins[1]);
      return this.translations.addManyTeamMembers(admins.slice(0, 2).join(', '), admins.length - 2);
    },
    [AutomationActionType.JobChangeStatus]: (statusName: string | null) => {
      if (!statusName) return this.translations.changeJobStatusBasic;
      return this.translations.changeJobStatus(statusName);
    },
    [AutomationActionType.JobSubmitToJobBoards]: (jobBoards: string[]) => {
      if (!jobBoards?.length) return this.translations.submitToJobBoardsBasic;
      if (jobBoards.length === 1) return this.translations.submitToJobBoardsOne(jobBoards[0]);
      if (jobBoards.length === 2)
        return this.translations.submitToJobBoardsTwo(jobBoards[0], jobBoards[1]);
      return this.translations.submitToJobBoardsMany(
        jobBoards.slice(0, 2).join(', '),
        jobBoards.length - 2,
      );
    },
  };
}
